import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import SectionTabs from "@/components/SectionTabs";
import ShareEntryDialog from "@/components/ShareEntryDialog";
import { Button } from "@/components/ui/button";
import { Trophy, CalendarDays, Users, Heart, Eye, Share2, ImageIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Talent's "Mes participations" — lists their contest entries.
 * Each item has a thumbnail, status, vote count, View + Share buttons.
 */
export default function MyContestEntries() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareCtx, setShareCtx] = useState(null); // { entryId, contestId }

  useEffect(() => {
    if (!authLoading && !user) navigate("/");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    api.get("/talent/my-contest-entries")
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch((err) => toast.error(err.response?.data?.detail || "Erreur de chargement"))
      .finally(() => setLoading(false));
  }, []);

  if (!user) return null;

  return (
    <AppShell>
      <SectionTabs
        eyebrow="L'agenda"
        title="Mes participations"
        subtitle="Retrouvez ici toutes vos soumissions aux concours Helencia, leurs votes et partagez-les en un clic."
        tabs={[
          { id: "events", to: "/events", label: "Événements", icon: CalendarDays },
          { id: "concours", to: "/talent/concours", label: "Concours", icon: Trophy },
          { id: "my-entries", to: "/talent/my-contest-entries", label: "Mes participations", icon: Heart },
          { id: "castings", to: "/talent/castings", label: "Castings", icon: Users },
        ]}
      />

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-[#C68E59] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl bg-white border border-dashed border-[#EADDC9] p-12 text-center">
          <ImageIcon className="w-10 h-10 text-[#C68E59] mx-auto opacity-60" />
          <p className="text-sm text-[#8B8680] mt-4">Vous n'avez encore participé à aucun concours.</p>
          <Link to="/talent/concours" className="inline-flex mt-5">
            <Button className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white">
              Découvrir les concours
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="my-entries-grid">
          {items.map((e) => (
            <EntryRow
              key={e.id}
              entry={e}
              onShare={() => setShareCtx({ entryId: e.id, contestId: e.contest_id })}
            />
          ))}
        </div>
      )}

      <ShareEntryDialog
        open={!!shareCtx}
        onClose={() => setShareCtx(null)}
        entryId={shareCtx?.entryId}
        contestId={shareCtx?.contestId}
      />
    </AppShell>
  );
}

function EntryRow({ entry, onShare }) {
  const c = entry.contest || {};
  const isActive = c.status === "active";
  return (
    <div className="bg-white rounded-2xl border border-[#EADDC9] overflow-hidden flex flex-col" data-testid={`my-entry-${entry.id}`}>
      <Link to={`/contest/${entry.contest_id}/entry/${entry.id}`} className="block relative group">
        <div className="aspect-[4/5] overflow-hidden bg-[#FAF3EA]">
          <img
            src={entry.image_url}
            alt={c.title || "Participation"}
            className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
            loading="lazy"
          />
        </div>
        <div className="absolute top-2 right-2 flex items-center gap-1 bg-white/95 backdrop-blur px-2 py-1 rounded-full text-xs font-semibold text-[#1A1A1A]">
          <Heart className="w-3.5 h-3.5 text-[#C68E59]" /> {entry.votes_count ?? 0}
        </div>
      </Link>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-center gap-2">
          <Trophy className="w-3.5 h-3.5 text-[#C68E59]" />
          <span className="text-[10px] uppercase tracking-wider text-[#A26F3E]">
            {c.theme || "Concours"}
          </span>
          {isActive ? (
            <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E7F5EC] text-[#2E7D32] border border-[#BFE4C5]">
              Actif
            </span>
          ) : (
            <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FAF3EA] text-[#8B8680] border border-[#E5E5E5]">
              Fermé
            </span>
          )}
        </div>
        <h3 className="font-serif text-lg text-[#1A1A1A] mt-2 leading-tight line-clamp-2">
          {c.title || "Concours"}
        </h3>
        {entry.description && (
          <p className="text-xs text-[#6E4B2A] mt-2 line-clamp-2 leading-snug">{entry.description}</p>
        )}

        <div className="mt-4 flex gap-2">
          <Link to={`/contest/${entry.contest_id}/entry/${entry.id}`} className="flex-1">
            <Button variant="outline" size="sm" className="w-full rounded-full border-[#EADDC9]" data-testid={`my-entry-view-${entry.id}`}>
              <Eye className="w-3.5 h-3.5 mr-1" /> Voir
            </Button>
          </Link>
          <Button
            size="sm"
            onClick={onShare}
            className="flex-1 rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white"
            data-testid={`my-entry-share-${entry.id}`}
          >
            <Share2 className="w-3.5 h-3.5 mr-1" /> Partager
          </Button>
        </div>
      </div>
    </div>
  );
}
